import { usePermissions } from "Components/Hooks/usePermissions";
import { MENU_CONFIG, MenuConfigItem } from "common/data/menu";

/**
 * useNavData hook builds the runtime menu configuration.
 *
 * - Items with a role-based failure (allowed === false) are filtered (not shown).
 * - Items with subscription failure (locked is true, but allowed remains true) are included with a locked state.
 */
export const useNavData = (
  menuState: Record<string, boolean>,
  setMenuState: (cb: (prev: any) => any) => void,
  setCurrentState: (val: string) => void,
  updateIconSidebar: (e: any) => void
): any[] => {
  const { checkPermission } = usePermissions();

  const attachRuntimeProps = (item: MenuConfigItem): any => {
    const { id, permission, subItems } = item;
    // Use the menu item's id (or provided featureKey) for the subscription check.
    const featureKey = item.featureKey || id;
    const perm = checkPermission(permission.module, permission.action, featureKey);

    // Hide the item entirely if role-based permission fails.
    if (perm.allowed === false) {
      return null;
    }
    
    // Merge a potential custom tooltip text.
    const tooltipText = item.disabledTooltip || perm.reason;

    const newItem: any = {
      ...item,
      subItems: null,
      permissionState: perm,
      disabled: perm.locked, // For UI, disabled if locked.
      locked: perm.locked,   // Propagate locked flag.
      tooltipText,
      stateVariables: menuState[id],
      click: (e: any) => {
        e.preventDefault();
        setMenuState((prev) => ({ ...prev, [id]: !prev[id] }));
        setCurrentState(id);
        updateIconSidebar(e);
      },
    };

    if (subItems) {
      newItem.subItems = subItems
        .map((sub) => {
          const subFeatureKey = sub.featureKey || sub.id;
          const subPerm = checkPermission(sub.permission.module, sub.permission.action, subFeatureKey);
          
          // Hide sub-item if role-based permission fails.
          if (subPerm.allowed === false) return null;
          
          return {
            ...sub,
            permissionState: subPerm,
            disabled: subPerm.locked,
            locked: subPerm.locked,
            tooltipText: sub.disabledTooltip || subPerm.reason,
            stateVariables: menuState[sub.id],
            click: (e: any) => {
              e.preventDefault();
              setMenuState((prev) => ({ ...prev, [sub.id]: !prev[sub.id] }));
              setCurrentState(sub.id);
              updateIconSidebar(e);
            },
          };
        })
        .filter(Boolean);
    }

    return newItem;
  };

  return MENU_CONFIG.map(attachRuntimeProps).filter(Boolean);
};
